import React, { useState } from "react";
import { Popover, ListGroup } from "reactstrap";
import useAppBarStyles, { ListItem, AnchorLink } from "./styles";
import { hasPermission, permissions } from "../../utility/helpers/claimHelper";

interface AppBarMenuItemProps {
  targetElementMenu: string | HTMLElement;
  open?: boolean;
  toggleMenuPopover: () => void;
}

interface SubMenuItemProps {
  text: string;
  url: string;
  targetElementKey?: string;
  permissions?: string[];
}

interface MenuItemProps {
  text: string;
  url: string;
  subMenuItems?: SubMenuItemProps[];
  targetElementKey?: any;
  permissions?: string[];
}

const SingleLevel: React.FC<SubMenuItemProps> = ({
  text,
  url,
  targetElementKey,
  permissions,
}) => (
  <ListItem
    style={{ display: hasPermission(permissions) ? "block" : "none" }}
    onClick={() => {
      window.location.href = url;
    }}
  >
    <AnchorLink id={"link_" + targetElementKey} href={url}>
      {text}
    </AnchorLink>
  </ListItem>
);
const MultiLevel: React.FC<MenuItemProps> = ({
  targetElementKey,
  text,
  url,
  subMenuItems,
  permissions,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const appBarStyles = useAppBarStyles();
  const togglePopover = () => setPopoverOpen(!popoverOpen);
  return (
    <>
      <ListItem
        onClick={togglePopover}
        style={{ display: hasPermission(permissions) ? "block" : "none" }}
      >
        <div
          id={targetElementKey}
          className="dropdown-toggle"
          style={appBarStyles.customPopoverLink}
          onClick={togglePopover}
        >
          {text}
        </div>
      </ListItem>
      <Popover
        placement="bottom"
        trigger="legacy"
        isOpen={popoverOpen}
        target={targetElementKey}
        toggle={togglePopover}
        hideArrow
        style={appBarStyles.customPopover}
      >
        <ListGroup>
          {subMenuItems !== undefined ? (
            subMenuItems.map((subItem, index) => (
              <SingleLevel
                key={`${targetElementKey}-sub-${index}`}
                text={subItem.text}
                url={subItem.url}
                targetElementKey={`${targetElementKey}-sub-${index}`}
                permissions={subItem.permissions}
              />
            ))
          ) : (
            <>
              <ListItem
                style={{
                  display: hasPermission(permissions) ? "block" : "none",
                }}
                onClick={() => {
                  window.location.href = url;
                }}
              >
                <AnchorLink
                  className="text-dark"
                  style={appBarStyles.customPopoverLink}
                  href={url}
                >
                  {text}
                </AnchorLink>
              </ListItem>
            </>
          )}
        </ListGroup>
      </Popover>
    </>
  );
};

const MenuItem: React.FC<MenuItemProps> = ({
  text,
  url,
  subMenuItems,
  targetElementKey,
  permissions,
}) => {
  if (text !== undefined && url !== undefined) {
    const Component = subMenuItems !== undefined ? MultiLevel : SingleLevel;
    return (
      <Component
        targetElementKey={targetElementKey}
        text={text}
        url={url}
        subMenuItems={subMenuItems}
        permissions={permissions}
      />
    );
  }
};

const AppBarManageMenuItems: React.FC<AppBarMenuItemProps> = ({
  targetElementMenu,
  open,
  toggleMenuPopover,
}) => {
  const links: MenuItemProps[] = [
    {
      text: "Admin",
      url: "/admin",
      permissions: [
        permissions.ADMIN_LOOKUP,
        permissions.ADMIN_USER,
        permissions.MANAGE_DONOR_ADMIN,
      ],
      subMenuItems: [
        {
          text: "Role",
          url: "/settings-page/role",
          permissions: [permissions.ADMIN_USER],
        },
      ],
    },
    {
      text: "Claim",
      url: "/settings-page/claim",
      permissions: [permissions.ADMIN_USER],
    },
    {
      text: "County",
      url: "/settings-page/county",
      permissions: [permissions.ADMIN_USER],
    },
    {
      text: "Lookup",
      url: "/admin/lookup",
      permissions: [permissions.ADMIN_USER],
      subMenuItems: [
        {
          text: "Lookup Type",
          url: "/admin/lookup/lookuptype",
          permissions: [permissions.ADMIN_LOOKUP],
        },
        {
          text: "Lookup Value",
          url: "/admin/lookup/lookupvalue",
          permissions: [permissions.ADMIN_USER],
        },
        {
          text: "Lookup Group",
          url: "/admin/lookup/lookupgroup",
          permissions: [permissions.ADMIN_USER],
        },
      ],
    },
    {
      text: "States",
      url: "/settings-page/states",
      permissions: [permissions.ADMIN_USER],
    },
    {
      text: "Users",
      url: "/users",
      permissions: [permissions.ADMIN_USER],
    },
    {
      text: "Settings",
      url: "/settings-page/settings",
      permissions: [permissions.ADMIN_USER],
    },
    {
      text: "Reports",
      url: "/reports",
      permissions: [permissions.VIEW_REPORT],
      subMenuItems: [
        {
          text: "Champions",
          url: "/reports/champions",
          permissions: [permissions.VIEW_REPORT],
        },

        {
          text: "Employee Payroll Deductions",
          url: "/reports/employee_payroll_deductions",
          permissions: [permissions.VIEW_REPORT],
        },
        {
          text: "Campaigns",
          url: "/reports/campaigns",
          permissions: [permissions.VIEW_REPORT],
        },
        {
          text: "Pledges",
          url: "/reports/pledges",
          permissions: [permissions.VIEW_REPORT],
        },
      ],
    },
    /* 
    {
      text: "Export",
      url: "/export",
      permissions: [permissions.VIEW_EXCEL_REPORTS],
    },
    {
      text: "Employee Import",
      url: "/employeeimport",
      permissions: [permissions.IMPORT_EMPLOYEE_DATA],
    },
    {
      text: "Employees",
      url: "/employees",
      permissions: [permissions.MANAGE_EMPLOYEES],
    },
    {
      text: "Campaign",
      url: "/campaign",
      permissions: [permissions.MANAGE_CAMPAIGNS],
    },
    {
      text: "Organizations",
      url: "/organization",
      permissions: [permissions.MANAGE_DONOR_ADMIN, permissions.ADMIN_USER],
    },*/
  ];
  const appBarStyles = useAppBarStyles();

  return (
    <>
      <Popover
        placement="bottom"
        isOpen={open}
        trigger="legacy"
        target={targetElementMenu}
        toggle={toggleMenuPopover}
        style={appBarStyles.customPopover}
        hideArrow
      >
        <style>
          {`
      .popover {
        border-radius: 10px !important;
        border: 1px solid #ccc !important;
        overflow: hidden; 
      }
    `}
        </style>
        <ListGroup>
          {links.map((item, index) => (
            <div key={index}>
              {hasPermission(item.permissions) === true && (
                <MenuItem
                  targetElementKey={`menu-item-${index}`}
                  text={item.text}
                  url={item.url}
                  subMenuItems={item.subMenuItems}
                  permissions={item.permissions}
                  key={index}
                />
              )}
            </div>
          ))}
        </ListGroup>
      </Popover>
    </>
  );
};

export default AppBarManageMenuItems;
